import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Drop-in replacement for the `react-hook-clipboard` package.
 *
 * The original hook had two problems that made the copy buttons unreliable:
 *
 * 1) it awaited `navigator.permissions.query({ name: 'clipboard-write' })`
 *    before calling `writeText`, so the write happened outside the user
 *    gesture and was skipped entirely when `navigator.permissions` was
 *    missing or the query rejected. A skipped write leaves the previous
 *    content in the clipboard, so the next paste returns the previously
 *    copied value;
 * 2) it polled `navigator.clipboard.readText()` every second, for every
 *    mounted instance of the hook (one per lead field, one per table row).
 *
 * Here the write is issued synchronously inside the click handler, falls back
 * to `document.execCommand('copy')` when the async API is unavailable or
 * rejects, and reports whether it actually succeeded.
 */

// Shared across every instance of the hook: the clipboard is a single
// resource, so concurrent copies from different buttons must be ordered.
let latestRequestId = 0;
let latestText = '';

const MAX_REORDER_FIXES = 3;

const toClipboardText = (content) =>
  content === null || content === undefined ? '' : String(content);

// The fallback is the last resort of the copy path, so it must never throw:
// every failure is reported as `false` and the temporary node is always
// removed, even when the copy itself blows up.
const legacyCopy = (text) => {
  if (typeof document === 'undefined' || !document.body) return false;

  const activeElement = document.activeElement;
  let textArea = null;
  let copied = false;

  try {
    textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.setAttribute('readonly', '');
    textArea.style.position = 'fixed';
    textArea.style.top = '-1000px';
    textArea.style.opacity = '0';
    document.body.appendChild(textArea);

    textArea.select();
    textArea.setSelectionRange(0, textArea.value.length);
    copied = document.execCommand('copy');
  } catch (error) {
    copied = false;
  } finally {
    if (textArea && textArea.parentNode)
      textArea.parentNode.removeChild(textArea);
    if (activeElement && typeof activeElement.focus === 'function')
      activeElement.focus();
  }

  return copied;
};

const writeText = (text, requestId, fixesLeft = MAX_REORDER_FIXES) => {
  const clipboardApi =
    typeof navigator !== 'undefined' ? navigator.clipboard : null;

  if (!clipboardApi || !clipboardApi.writeText)
    return Promise.resolve(legacyCopy(text));

  let write;
  try {
    write = Promise.resolve(clipboardApi.writeText(text));
  } catch (error) {
    return Promise.resolve(legacyCopy(text));
  }

  return write.then(
    () => {
      // A newer copy was requested while this write was in flight: the two
      // writes can land in any order, so re-apply the most recent value.
      if (requestId !== latestRequestId && fixesLeft > 0)
        return writeText(latestText, latestRequestId, fixesLeft - 1);

      return true;
    },
    () => legacyCopy(text),
  );
};

const useClipboard = () => {
  const [clipboard, setClipboard] = useState('');
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const copyToClipboard = useCallback(
    (content, onCopyToClipboard = () => {}, onWriteError = () => {}) => {
      const text = toClipboardText(content);

      latestRequestId += 1;
      latestText = text;
      const requestId = latestRequestId;

      return writeText(text, requestId).then((copied) => {
        if (!copied) {
          onWriteError({
            message: 'Unable to write to the clipboard.',
            content: text,
          });
          return false;
        }

        if (isMounted.current) setClipboard(text);
        onCopyToClipboard(text);
        return true;
      });
    },
    [],
  );

  return [clipboard, copyToClipboard];
};

export default useClipboard;
